import { useRef, useState } from 'react';
import { Flex, InputBox, Typography } from '@banhala/ui-kit';
import { ApiError } from 'shared/src/api/httpClient';
import { useInterval } from 'shared/src/hooks';
import { bridge } from 'shared/src/utils';
import { usePostVerificationIssue } from './queries/usePostVerificationIssue';
import { usePostVerificationValidate } from './queries/usePostVerificationValidate';

const MAX_VERIFY_COUNT = 5;
const PHONE_REGEX = /^01\d{8,9}$/;

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

type Props = {
  /** 인증 완료된 휴대폰 번호를 상위 폼에 전달 */
  onVerified: (phoneNumber: string) => void;
};

/**
 * SMS 휴대폰 인증 (가입 보완 드로어). (webview RegisterDrawer/MobileForm 대응)
 * 발급 → 카운트다운 → 검증. 검증 응답 registered_member 있으면 번호 점유로 차단,
 * 없으면 인증 완료 처리. 검증 5회 초과 시 재발급 유도.
 */
export const MobileForm = ({ onVerified }: Props) => {
  const { mutateAsync: issue } = usePostVerificationIssue();
  const { mutateAsync: validate } = usePostVerificationValidate();

  const [phone, setPhone] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [publicCode, setPublicCode] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [verified, setVerified] = useState(false);
  const [deadline, setDeadline] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const verifyCountRef = useRef(0);
  // 발급/검증 in-flight 중복 실행(더블탭) 차단.
  const busyRef = useRef(false);

  const secondsLeft = deadline > now ? Math.ceil((deadline - now) / 1000) : 0;
  const ticking = waiting && !verified && secondsLeft > 0;
  useInterval(() => setNow(Date.now()), ticking ? 1000 : null);

  const isPhoneValid = PHONE_REGEX.test(phone);

  // 번호를 바꾸면 이전 번호로 발급된 코드가 새 번호에 쓰이지 않도록 인증 상태를 리셋한다.
  // (인증 완료 후에는 input이 잠기므로 대기 중일 때만 발생)
  const handlePhoneChange = (value: string) => {
    setPhone(value);
    if (waiting) {
      setWaiting(false);
      setPublicCode('');
      setAuthCode('');
      setDeadline(0);
      verifyCountRef.current = 0;
    }
  };

  const handleIssue = async () => {
    if (busyRef.current || verified || !isPhoneValid) return;
    busyRef.current = true;
    try {
      const { public_code, remain_seconds } = await issue({
        phone_number: phone,
      });
      // 발급/만료 기준 시각을 한 번만 찍어 카운트다운 초기값 오차를 없앤다.
      const startedAt = Date.now();
      setPublicCode(public_code);
      setNow(startedAt);
      setDeadline(startedAt + remain_seconds * 1000);
      setAuthCode('');
      setWaiting(true);
      // webview와 동일: 이미 최대치일 때만 리셋(재발급으로 시도횟수를 무한 초기화하지 않음).
      if (verifyCountRef.current >= MAX_VERIFY_COUNT) {
        verifyCountRef.current = 0;
      }
    } catch (error) {
      bridge.toast(
        error instanceof ApiError && error.message
          ? error.message
          : '인증번호 발급에 실패했어요',
      );
    } finally {
      busyRef.current = false;
    }
  };

  const handleVerify = async () => {
    if (busyRef.current || verified || !waiting || authCode === '') return;
    if (secondsLeft === 0) return;
    if (verifyCountRef.current >= MAX_VERIFY_COUNT) {
      bridge.toast('인증 요청 횟수를 초과했어요. 인증번호를 다시 받아주세요.');
      return;
    }
    busyRef.current = true;
    try {
      const { registered_member } = await validate({
        public_code: publicCode,
        auth_code: authCode,
        auth_context: 'SIGN_UP',
      });
      if (registered_member != null) {
        // 번호 점유 — 인증 확정하지 않고 차단.
        bridge.toast(
          '이미 가입된 휴대폰 번호입니다. 계정 정보 확인은 아이디찾기를 이용해 주세요.',
        );
        return;
      }
      setVerified(true);
      setWaiting(false);
      onVerified(phone);
      bridge.toast('인증되었어요');
    } catch (error) {
      verifyCountRef.current += 1;
      bridge.toast(
        error instanceof ApiError && error.message
          ? error.message
          : '인증에 실패했어요',
      );
    } finally {
      busyRef.current = false;
    }
  };

  return (
    <Flex gap={8}>
      <Typography type={'subtitle2'} color={'content_primary'}>
        {'휴대폰 번호'}
      </Typography>
      <Flex horizontal alignItems={'center'} gap={8}>
        <Flex flex={1}>
          <InputBox
            type={'underline'}
            placeholder={'휴대폰 번호 입력 (- 없이)'}
            value={phone}
            onChangeText={handlePhoneChange}
            keyboardType={'number-pad'}
            editable={!verified}
          />
        </Flex>
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
          height={44}
          padding={{ horizontal: 16 }}
          borderRadius={8}
          backgroundColor={
            !isPhoneValid || verified ? 'bg_secondary' : 'bg_black_button'
          }
          onPress={handleIssue}
        >
          <Typography
            type={'body3'}
            color={
              !isPhoneValid || verified
                ? 'content_tertiary'
                : 'content_always_white'
            }
          >
            {waiting ? '다시받기' : '인증번호 받기'}
          </Typography>
        </Flex>
      </Flex>

      {waiting && !verified && (
        <Flex horizontal alignItems={'center'} gap={8}>
          <Flex flex={1}>
            <InputBox
              type={'underline'}
              placeholder={'인증번호 입력'}
              value={authCode}
              onChangeText={setAuthCode}
              keyboardType={'number-pad'}
              rightElement={
                <Typography type={'body3'} color={'content_pink_primary'}>
                  {formatTime(secondsLeft)}
                </Typography>
              }
            />
          </Flex>
          <Flex
            alignItems={'center'}
            justifyContent={'center'}
            height={44}
            padding={{ horizontal: 16 }}
            borderRadius={8}
            backgroundColor={
              authCode === '' || secondsLeft === 0
                ? 'bg_secondary'
                : 'bg_black_button'
            }
            onPress={handleVerify}
          >
            <Typography
              type={'body3'}
              color={
                authCode === '' || secondsLeft === 0
                  ? 'content_tertiary'
                  : 'content_always_white'
              }
            >
              {'확인'}
            </Typography>
          </Flex>
        </Flex>
      )}

      {verified && (
        <Typography type={'body3'} color={'content_pink_primary'}>
          {'본인 인증이 완료되었어요'}
        </Typography>
      )}
    </Flex>
  );
};
